/**
 * The document metadata framework.
 *
 * 25 document types share one chassis. The source system stores numbering,
 * workflows, print templates and permissions as ROWS KEYED BY DOCUMENT TYPE —
 * so those are runtime configuration, not code. A new document type is a
 * `defineDocument()` call and nothing else.
 */
import type { TabKey, ActionKey } from '../enums/index.js'

export type ControlKind =
  | 'text' | 'textarea' | 'number' | 'money' | 'qty' | 'rate' | 'percent'
  | 'date' | 'select' | 'radio' | 'checkbox' | 'lookup'

export interface FieldDef {
  key: string
  label: string
  control: ControlKind
  required?: boolean
  hint?: string
  /** Server-side searchable reference to a master entity. */
  lookup?: { entity: string; labelField?: string }
  options?: ReadonlyArray<{ value: string; label: string }>
  width?: 'full' | 'half'
  /** Locked once the document leaves DRAFT. */
  lockAfterDraft?: boolean
  defaultValue?: string
  /**
   * Shown only while another field on the same form holds one of these values.
   * A purchase order for stock has no project and every warehouse question a
   * project order does not ask; without this the wizard demands both.
   *
   * `equals` matches a fixed list. `isSet` matches "anything at all", which is
   * what an exchange rate needs: it is asked for whenever a currency has been
   * chosen, whichever one it is.
   */
  visibleWhen?: {
    readonly field: string
    readonly equals?: readonly string[]
    readonly isSet?: boolean
  }
  /** Shown only when the company has this setting on. */
  requiresSetting?: string
}

/** Is this field asked for, given what has been answered and switched on? */
export const isFieldVisible = (
  field: FieldDef,
  values: Readonly<Record<string, unknown>>,
  settings?: Readonly<Record<string, boolean | number | string>>,
): boolean => {
  if (field.requiresSetting && settings && settings[field.requiresSetting] !== true) return false
  const when = field.visibleWhen
  if (!when) return true
  const held = String(values[when.field] ?? '')
  if (when.isSet) return held !== ''
  return (when.equals ?? []).includes(held)
}

export interface SectionDef {
  key: string
  /** Section number, printed identically on screen and on the PDF. */
  n?: string
  title: string
  fields: readonly FieldDef[]
}

export type ColumnType = 'text' | 'docno' | 'date' | 'money' | 'qty' | 'rate' | 'status' | 'user' | 'bool'

export interface ColumnDef {
  key: string
  label: string
  type?: ColumnType
  width?: number
  /** Money and quantity columns right-align with tabular figures. */
  align?: 'left' | 'right'
}

export interface LineColumnDef extends ColumnDef {
  editable?: boolean
  /** Rendered under the primary cell text, e.g. an item description. */
  secondary?: boolean
  /** A fixed-choice cell rather than free text. */
  options?: ReadonlyArray<{ value: string; label: string }>
  /**
   * A type-ahead against a master, for cells that hold an id.
   *
   * `fills` copies what the pick carries onto other cells of the row when
   * those are still empty — an adjustment's direction from its master, for
   * instance: `{ direction: 'direction' }` reads the pick's `direction` into
   * the row's `direction`.
   */
  lookup?: { entity: string; fills?: Readonly<Record<string, string>> }
  /**
   * A picker over the rows of another grid on the same document — an
   * estimation line choosing the BOQ line it costs, a BOQ line choosing the
   * billing term it is billed under.
   *
   * The rows come from `picksFrom`, or from the grid's own `parentSection`
   * when that is not set. The value stored is the chosen row's id, or its
   * `seq:<SECTION>:<n>` key while both are still unsaved.
   */
  parentPicker?: boolean
  picksFrom?: LineSectionKey
  hint?: string
  /**
   * A cell the grid keeps in step as the operator types, because it is
   * nothing but arithmetic on two others: a receipt's accepted quantity is
   * what was delivered less what was rejected. The server works the same
   * figure out on save and is the authority; this is so the row and its
   * amount read right before then.
   */
  formula?:
    /** `floor` keeps it from going below a figure — an accepted quantity is never negative; a variance may be. */
    | { readonly subtract: readonly [string, string]; readonly floor?: string }
    | { readonly sum: readonly string[] }
}

export interface FilterDef {
  key: string
  label: string
  control: 'text' | 'select' | 'dateRange' | 'lookup' | 'status'
  required?: boolean
  lookup?: { entity: string }
  options?: ReadonlyArray<{ value: string; label: string }>
}

/**
 * The storage sections a document's lines fall into.
 *
 * One document carries several grids — a project carries its BOQ, the estimate
 * under it and its billing terms; an invoice carries its items, its additional
 * charges and its deductions. They are all `document_line` rows separated by
 * this, which is why a second grid costs a definition rather than a table.
 */
export const LINE_SECTIONS = [
  'ITEM', 'BOQ', 'ESTIMATION', 'CHARGE', 'ADJUSTMENT', 'BILLING_TERM', 'PARTICIPANT',
  'SUMMARY_ITEM',
  // The joint measurement record behind a claimed progress quantity.
  'MEASUREMENT',
] as const
export type LineSectionKey = (typeof LINE_SECTIONS)[number]

/** Where line items may be pulled from, besides typing them by hand. */
export interface LineSourceDef {
  key: string
  label: string
  /** The picker queries this endpoint for candidate lines. */
  source:
    | 'estimation' | 'request' | 'stock' | 'order' | 'boq' | 'excel'
    // docs/24 — the supply-chain pickers.
    | 'requisition' | 'issue' | 'rejection' | 'book' | 'audit' | 'criteria'
}

/** One line grid on a document. */
export interface LineGridDef {
  /** Storage section, and the grid's key on the form. Defaults to ITEM. */
  section?: LineSectionKey
  title: string
  n?: string
  columns: readonly LineColumnDef[]
  sources: readonly LineSourceDef[]
  /**
   * Rows here belong to a row in that grid — estimation lines under a BOQ
   * line. The client offers a parent picker; the server stores parent_line_id.
   */
  parentSection?: LineSectionKey
  /** How this grid moves the document total. Defaults to ADD. */
  totalsAs?: 'ADD' | 'SUBTRACT' | 'NONE'
  /** Wording on the button that appends a row. */
  addLabel?: string
  /** Shown only when the company has this setting on. */
  requiresSetting?: string
}

export interface NextDocDef {
  docType: string
  label: string
  /** Only offered once the parent reaches this status. */
  whenStatus?: 'APPROVED' | 'IN_REVIEW'
}

/** The five groups the Transactions menu is organised into (docs/04). */
export type DocumentGroup =
  | 'Project'
  | 'Purchase and Expense'
  | 'Invoice'
  | 'Inventory'
  | 'Tools and Equipment'

export interface DocumentDef {
  /** Stable machine key. Also the `doc_type` discriminator in the database. */
  key: string
  /** Which section of the Transactions menu this appears under. */
  group: DocumentGroup
  /** One or two lines describing it, shown on the menu tile. */
  blurb: string
  /** URL segment: /app/:companyId/page/:route */
  route: string
  label: string
  labelPlural: string
  /** Numbering series key; the series itself is a row, editable per company. */
  numbering: string
  /**
   * Prefix used when a company has no series row yet, so a new document type
   * needs no hand-seeded configuration. `{CO}` is replaced by the company code.
   * Formats follow docs/14. A company may edit the series afterwards.
   */
  numberPrefix: string
  numberWidth?: number
  /** Workflow definition key; resolved and SNAPSHOTTED onto each document. */
  workflow: string
  tabs: readonly TabKey[]
  createWizard: { title: string; subtitle?: string; fields: readonly FieldDef[] }
  form: { sections: readonly SectionDef[] }
  /** The primary grid. Section defaults to ITEM. */
  lines?: LineGridDef
  /** Further grids on the same document: charges, deductions, the estimate. */
  grids?: readonly LineGridDef[]
  /** Default list columns. A saved view overrides these per user. */
  columns: readonly ColumnDef[]
  filters: readonly FilterDef[]
  actions: readonly ActionKey[]
  /** Rule keys evaluated on submit. Definitions live in the rules engine. */
  rules: readonly string[]
  /**
   * A setting this type depends on. When it is off the type stays routable —
   * old links and existing documents still open — but it is off the menu.
   */
  requiresSetting?: string
  /** Set by the server per company, from `requiresSetting`. Never authored. */
  hidden?: boolean
  /** Documents this one can be raised from, and raised into. */
  createsNext?: readonly NextDocDef[]
  /** Does this document allocate against another document's lines? */
  allocatesFrom?: readonly string[]
}

export const defineDocument = <const T extends DocumentDef>(def: T): T => def
